
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION } from '../utils/version';
import NoTranslate from '../components/NoTranslate';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import {
  formatDisplayDate,
  formatDisplayDateTime,
  resolveLeaveTypeName
} from '../utils/localeFormat';

function ApproverDashboard() {
  const { user } = useAuth();
  const leaveTypes = useLeaveTypes();
  const [pendingApplications, setPendingApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  
  // Approval/Rejection Modal
  const [approvalModal, setApprovalModal] = useState({
    show: false,
    application: null,
    action: '', // 'approve' or 'reject'
    notes: ''
  });

  // Audit Modal
  const [auditModal, setAuditModal] = useState({
    show: false,
    application: null,
    history: []
  });

  const fetchPendingApplications = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/applications/pending-approvals/${user.id}`);
      setPendingApplications(response.data);
    } catch (error) {
      console.error('Error fetching pending applications:', error);
      toast.error('Failed to fetch pending applications');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.is_approver) {
      fetchPendingApplications();
    }
  }, [user, fetchPendingApplications]);

  const openApprovalModal = (application, action) => {
    setApprovalModal({
      show: true,
      application,
      action,
      notes: ''
    });
  };

  const closeApprovalModal = () => {
    setApprovalModal({
      show: false,
      application: null,
      action: '',
      notes: ''
    });
  };

  const openAuditModal = async (application) => {
    try {
      const response = await api.get(`/applications/${application.id}/history`);
      setAuditModal({
        show: true,
        application,
        history: response.data
      });
    } catch (error) {
      console.error('Application history fetch error:', error);
      toast.error('Failed to fetch application history');
    }
  };

  const closeAuditModal = () => {
    setAuditModal({
      show: false,
      application: null,
      history: []
    });
  };

  const handleApproval = async () => {
    if (!approvalModal.application) return;

    try {
      setProcessingId(approvalModal.application.id);
      
      await api.post(`/applications/${approvalModal.application.id}/approve`, {
        action: approvalModal.action,
        notes: approvalModal.notes
      });

      toast.success(
        approvalModal.action === 'approve' 
          ? 'Application approved' 
          : 'Application rejected'
      );
      
      closeApprovalModal();
      fetchPendingApplications();
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error('Failed to process approval');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    return formatDisplayDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getTotalDays = (details) => {
    if (!details || details.length === 0) return 0;
    return details.reduce((total, detail) => total + (detail.hours / 8), 0);
  };

  if (!user?.is_approver) {
    return (
      <div className="card">
        <h3>Access Denied</h3>
        <p>This page is only accessible to approvers.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Pending Approvals</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
      </div>

      {pendingApplications.length === 0 ? (
        <div className="card text-center">
          <h3>No Pending Applications</h3>
          <p>There are currently no leave applications requiring approval.</p>
        </div>
      ) : (
        <div className="applications-grid">
          {pendingApplications.map(application => (
            <div key={application.id} className="card application-card">
              <div className="application-header">
                <div className="applicant-info">
                  <NoTranslate as="h4">{application.applicant_name}</NoTranslate>
                  <NoTranslate className="department">{application.applicant_department || 'No Department'}</NoTranslate>
                </div>
                <div className="application-status">
                  <NoTranslate className="badge badge-warning">Pending</NoTranslate>
                </div>
              </div>

              <div className="application-details">
                <div className="detail-row">
                  <label>Application ID</label>
                  <span>#{application.id}</span>
                </div>
                <div className="detail-row">
                  <label>Leave Type</label>
                  <span className="leave-type" style={{backgroundColor: application.color_code}}>
                    <NoTranslate>{resolveLeaveTypeName(application, leaveTypes)}</NoTranslate>
                  </span>
                </div>

                <div className="detail-row">
                  <label>Period</label>
                  <span>
                    {formatDate(application.start_date)} - {formatDate(application.end_date)}
                    <span className="total-days"> ({getTotalDays(application.details)} days)</span>
                  </span>
                </div>

                <div className="detail-row">
                  <label>Applied Date</label>
                  <span>{formatDate(application.applied_at)}</span>
                </div>

                {application.requested_approver_name && (
                  <div className="detail-row">
                    <label>Approver</label>
                    <NoTranslate>
                      {application.requested_approver_name}
                      {application.requested_approver_department && ` (${application.requested_approver_department})`}
                    </NoTranslate>
                  </div>
                )}

                {application.reason && (
                  <div className="detail-row">
                    <label>Reason</label>
                    <span>{application.reason}</span>
                  </div>
                )}

                {application.details && application.details.length > 0 && (
                  <div className="detail-row">
                    <label>Details</label>
                    <div className="leave-details">
                      {application.details.map((detail, index) => (
                        <span key={index} className="detail-item">
                          {formatDate(detail.leave_date)} 
                          ({detail.period === 'full' ? 'Full Day' : detail.period === 'am' ? 'AM' : 'PM'})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(application.certificate_filename || application.certificate_google_drive_url) && (
                  <div className="detail-row">
                    <label>Certificate</label>
                    <div>
                      {application.certificate_google_drive_url ? (
                        <a 
                          href={application.certificate_google_drive_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="certificate-link"
                        >
                          📎 View Medical Certificate (Google Drive)
                        </a>
                      ) : (
                        <a 
                          href={`/uploads/${application.certificate_filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="certificate-link"
                        >
                          📎 View Medical Certificate
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {(application.other_paid_leave_document_filename ||
                  application.other_paid_leave_document_google_drive_url) &&
                  application.leave_type_code === 'OTHER_PAID' && (
                  <div className="detail-row">
                    <label>Supporting document</label>
                    <div>
                      {application.other_paid_leave_document_google_drive_url ? (
                        <a
                          href={application.other_paid_leave_document_google_drive_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="certificate-link"
                        >
                          📎 View (Google Drive)
                        </a>
                      ) : (
                        <a
                          href={`/uploads/${application.other_paid_leave_document_filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="certificate-link"
                        >
                          📎 View document
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="application-actions">
                <button
                  className="btn btn-info btn-sm"
                  onClick={() => openAuditModal(application)}
                >
                  View History
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => openApprovalModal(application, 'approve')}
                  disabled={processingId === application.id}
                >
                  Approve
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => openApprovalModal(application, 'reject')}
                  disabled={processingId === application.id}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approval/Rejection Modal */}
      {approvalModal.show && (
        <div className="modal-overlay" onClick={closeApprovalModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>
              {approvalModal.action === 'approve' ? 'Approve Application' : 'Reject Application'}
            </h4>
            
            <div className="modal-application-info">
              <p><strong>Application ID:</strong> #{approvalModal.application?.id}</p>
              <p><strong>Applicant:</strong> <NoTranslate>{approvalModal.application?.applicant_name}</NoTranslate></p>
              <p><strong>Leave Type:</strong> <NoTranslate>{resolveLeaveTypeName(approvalModal.application, leaveTypes)}</NoTranslate></p>
              <p><strong>Period:</strong> {formatDate(approvalModal.application?.start_date)} - {formatDate(approvalModal.application?.end_date)}</p>
            </div>

            <div className="form-group">
              <label className="form-label">
                {approvalModal.action === 'approve' ? 'Approval Comment' : 'Rejection Reason'} 
                {approvalModal.action === 'reject' && ' *'}
              </label>
              <textarea
                className="form-control"
                rows="4"
                value={approvalModal.notes}
                onChange={(e) => setApprovalModal(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={
                  approvalModal.action === 'approve' 
                    ? 'Optional comment' 
                    : 'Please enter the reason for rejection'
                }
                required={approvalModal.action === 'reject'}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeApprovalModal}>
                Cancel
              </button>
              <button 
                type="button" 
                className={`btn ${approvalModal.action === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleApproval}
                disabled={approvalModal.action === 'reject' && !approvalModal.notes.trim()}
              >
                {approvalModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Modal */}
      {auditModal.show && (
        <div className="modal-overlay" onClick={closeAuditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Application History</h4>
            
            <div className="modal-application-info">
              <p><strong>Application ID:</strong> #{auditModal.application?.id}</p>
              <p><strong>Applicant:</strong> <NoTranslate>{auditModal.application?.applicant_name}</NoTranslate></p>
              <p><strong>Leave Type:</strong> <NoTranslate>{resolveLeaveTypeName(auditModal.application, leaveTypes)}</NoTranslate></p>
            </div>

            <div className="audit-history">
              {auditModal.history.length > 0 ? (
                <div className="history-timeline">
                  {auditModal.history.map((record, index) => (
                    <div key={index} className="history-item">
                      <div className="history-date">
                        {formatDisplayDateTime(record.created_at || record.applied_at)}
                      </div>
                      <div className="history-action">
                        <strong>{record.action || 'Application Submitted'}</strong>
                        {record.performed_by && (
                          <>
                            {' '}by <NoTranslate>{record.performed_by}</NoTranslate>
                          </>
                        )}
                      </div>
                      {record.notes && (
                        <div className="history-notes">
                          <em>"{record.notes}"</em>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p>No history records found.</p>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeAuditModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pending-count {
          font-size: 1.1rem;
        }
        
        .applications-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 1.5rem;
        }
        
        .application-card {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 1.5rem;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          transition: box-shadow 0.2s;
        }
        
        .application-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }
        
        .application-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e9ecef;
        }
        
        .applicant-info h4 {
          margin: 0 0 0.25rem 0;
          color: #343a40;
        }
        
        .department {
          color: #6c757d;
          font-size: 0.9rem;
        }
        
        .application-details {
          margin-bottom: 1.5rem;
        }
        
        .detail-row {
          display: flex;
          margin-bottom: 0.75rem;
          align-items: flex-start;
        }
        
        .detail-row label {
          font-weight: 600;
          min-width: 80px;
          color: #495057;
          font-size: 0.9rem;
        }
        
        .detail-row span {
          color: #212529;
        }
        
        .leave-type {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          color: white;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .total-days {
          margin-left: 0.5rem;
          color: #6c757d;
          font-size: 0.9rem;
        }
        
        .leave-details {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
        }
        
        .detail-item {
          background-color: #f8f9fa;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.85rem;
          color: #495057;
        }
        
        .application-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #e9ecef;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          padding: 2rem;
          border-radius: 8px;
          min-width: 500px;
          max-width: 90vw;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .modal-application-info {
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 4px;
          margin-bottom: 1rem;
        }
        
        .modal-application-info p {
          margin: 0.25rem 0;
        }
        
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        
        .audit-history {
          max-height: 400px;
          overflow-y: auto;
          margin: 1rem 0;
        }
        
        .history-timeline {
          border-left: 2px solid #dee2e6;
          padding-left: 1rem;
        }
        
        .history-item {
          margin-bottom: 1.5rem;
          position: relative;
        }
        
        .history-item::before {
          content: '';
          position: absolute;
          left: -1.5rem;
          top: 0.5rem;
          width: 10px;
          height: 10px;
          background-color: #007bff;
          border-radius: 50%;
        }
        
        .history-date {
          color: #6c757d;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }
        
        .history-action {
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.25rem;
        }
        
        .history-notes {
          color: #6c757d;
          font-size: 0.9rem;
          margin-left: 1rem;
        }
        
        .certificate-link {
          color: #007bff;
          text-decoration: none;
        }
        
        .certificate-link:hover {
          text-decoration: underline;
        }
        
        @media (max-width: 768px) {
          .applications-grid {
            grid-template-columns: 1fr;
          }
          
          .application-header {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .application-actions {
            justify-content: stretch;
          }
          
          .modal-content {
            min-width: auto;
            margin: 1rem;
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default ApproverDashboard;
